/**
 * Local-first persistence (spec 41).
 *
 * Everything the user creates -- saved systems, projects, preferences, imported
 * measurement data, custom profiles, listening-test results -- lives in
 * IndexedDB on their machine. No account, no server, and audio never leaves the
 * browser (spec 98).
 *
 * Falls back to localStorage for the small stores if IndexedDB is unavailable
 * (private browsing in some engines), and degrades to in-memory if both are.
 */

import type { AnyProfile } from '../types/hardware';
import type { ChainSnapshot, ProjectFile, SwitchEvent } from '../types/system';

const DB_NAME = 'dac-engine';
const DB_VERSION = 1;

export const STORES = {
  projects: 'projects',
  systems: 'systems',
  profiles: 'profiles',
  measurements: 'measurements',
  prefs: 'prefs',
  history: 'history',
  listeningTests: 'listeningTests',
} as const;

type StoreName = (typeof STORES)[keyof typeof STORES];

let dbPromise: Promise<IDBDatabase | null> | null = null;
const memory = new Map<string, Map<string, unknown>>();

function memStore(name: string): Map<string, unknown> {
  if (!memory.has(name)) memory.set(name, new Map());
  return memory.get(name)!;
}

function openDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') {
      resolve(null);
      return;
    }
    let settled = false;
    const finish = (v: IDBDatabase | null) => {
      if (!settled) {
        settled = true;
        resolve(v);
      }
    };
    try {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        for (const name of Object.values(STORES)) {
          if (!db.objectStoreNames.contains(name)) db.createObjectStore(name);
        }
      };
      req.onsuccess = () => finish(req.result);
      req.onerror = () => finish(null);
      req.onblocked = () => finish(null);
      // Private-mode engines can hang rather than error.
      setTimeout(() => finish(null), 3000);
    } catch {
      finish(null);
    }
  });
  return dbPromise;
}

async function tx<T>(
  store: StoreName,
  mode: IDBTransactionMode,
  fn: (s: IDBObjectStore) => IDBRequest<T>,
): Promise<T | null> {
  const db = await openDb();
  if (!db) return null;
  return new Promise<T | null>((resolve) => {
    try {
      const t = db.transaction(store, mode);
      const req = fn(t.objectStore(store));
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      t.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

export async function put<T>(store: StoreName, key: string, value: T): Promise<boolean> {
  memStore(store).set(key, value);
  const r = await tx(store, 'readwrite', (s) => s.put(value as unknown as never, key));
  return r !== null;
}

export async function get<T>(store: StoreName, key: string): Promise<T | null> {
  const r = await tx<T>(store, 'readonly', (s) => s.get(key) as IDBRequest<T>);
  if (r !== null && r !== undefined) return r;
  return (memStore(store).get(key) as T) ?? null;
}

export async function del(store: StoreName, key: string): Promise<void> {
  memStore(store).delete(key);
  await tx(store, 'readwrite', (s) => s.delete(key));
}

export async function keys(store: StoreName): Promise<string[]> {
  const r = await tx<IDBValidKey[]>(store, 'readonly', (s) => s.getAllKeys());
  if (r) return r.map(String);
  return [...memStore(store).keys()];
}

export async function all<T>(store: StoreName): Promise<T[]> {
  const r = await tx<T[]>(store, 'readonly', (s) => s.getAll() as IDBRequest<T[]>);
  if (r) return r;
  return [...memStore(store).values()] as T[];
}

export async function isPersistent(): Promise<{ available: boolean; kind: string }> {
  const db = await openDb();
  if (db) return { available: true, kind: 'IndexedDB' };
  try {
    localStorage.setItem('__dac_probe', '1');
    localStorage.removeItem('__dac_probe');
    return { available: true, kind: 'localStorage (IndexedDB unavailable)' };
  } catch {
    return { available: false, kind: 'in-memory only -- nothing will survive a reload' };
  }
}

export async function estimateUsage(): Promise<{ usedBytes: number; quotaBytes: number } | null> {
  if (typeof navigator === 'undefined' || !navigator.storage?.estimate) return null;
  try {
    const e = await navigator.storage.estimate();
    return { usedBytes: e.usage ?? 0, quotaBytes: e.quota ?? 0 };
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Typed helpers
// ---------------------------------------------------------------------------

export interface SavedSystem {
  id: string;
  name: string;
  created: string;
  snapshot: ChainSnapshot;
  notes?: string;
}

export const saveProject = (p: ProjectFile) => put(STORES.projects, p.name, p);
export const loadProject = (name: string) => get<ProjectFile>(STORES.projects, name);
export const listProjects = () => all<ProjectFile>(STORES.projects);
export const deleteProject = (name: string) => del(STORES.projects, name);

export const saveSystem = (s: SavedSystem) => put(STORES.systems, s.id, s);
export const listSystems = () => all<SavedSystem>(STORES.systems);
export const deleteSystem = (id: string) => del(STORES.systems, id);

export const saveCustomProfile = (p: AnyProfile) => put(STORES.profiles, p.id, p);
export const listCustomProfiles = () => all<AnyProfile>(STORES.profiles);
export const deleteCustomProfile = (id: string) => del(STORES.profiles, id);

export const savePref = <T>(key: string, value: T) => put(STORES.prefs, key, value);
export const loadPref = <T>(key: string) => get<T>(STORES.prefs, key);

export async function appendHistory(events: SwitchEvent[]): Promise<void> {
  const existing = (await get<SwitchEvent[]>(STORES.history, 'switches')) ?? [];
  // Keep the log bounded: 2,000 switches is far more than any session needs.
  const next = [...existing, ...events].slice(-2000);
  await put(STORES.history, 'switches', next);
}

export const loadHistory = () => get<SwitchEvent[]>(STORES.history, 'switches');
export const clearHistory = () => del(STORES.history, 'switches');

/** Measurement blobs are stored separately: they can be megabytes each. */
export interface StoredMeasurement {
  id: string;
  kind: 'fr' | 'impulse' | 'impedance' | 'thd' | 'directivity' | 'room-ir';
  name: string;
  created: string;
  sampleRate?: number;
  channels?: number;
  /** Curve points or IR samples. */
  data: unknown;
  meta?: Record<string, unknown>;
}

export const saveMeasurement = (m: StoredMeasurement) => put(STORES.measurements, m.id, m);
export const listMeasurements = () => all<StoredMeasurement>(STORES.measurements);
export const getMeasurement = (id: string) => get<StoredMeasurement>(STORES.measurements, id);
export const deleteMeasurement = (id: string) => del(STORES.measurements, id);
